import { Box, IconButton, CircularProgress, Tooltip } from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CancelIcon from "@mui/icons-material/Cancel";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useEffect, useRef, useState } from "react";
import { uploadMediaWithProgress } from "./util";
import { getMediaKind, removeMediaKind, setMediaKind } from "./util";

type UploadItem = {
  id: string;
  name: string;
  progress: number;
  status: "uploading" | "error" | "canceled";
  abort: AbortController;
  preview?: string;
  isVideo?: boolean;
  error?: string;
};

type Props = {
  value: string[];
  onChange: (urls: string[]) => void;
  maxItems?: number;
  onKindsChange?: (info: { hasPhoto: boolean; hasVideo: boolean }) => void;
  onUrlKindsChange?: (kinds: Record<string, "image" | "video">) => void;
};

export default function MediaPicker({ value, onChange, maxItems = 10, onKindsChange, onUrlKindsChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [urlKinds, setUrlKinds] = useState<Record<string, "image" | "video">>({});
  const latestUrlsRef = useRef<string[]>(value);
  useEffect(() => { latestUrlsRef.current = value; }, [value]);

  useEffect(() => {
    return () => {
      uploads.forEach(u => u.preview && URL.revokeObjectURL(u.preview));
    };
  }, [uploads]);

  const triggerFileDialog = () => inputRef.current?.click();

  const extLooksLikeVideo = (url: string) =>
    /\.(mp4|mov|avi|mkv|webm)(\?|#|$)/i.test(url);

  const kindOfUrl = (url: string) =>
    urlKinds[url] ?? getMediaKind(url) ?? (extLooksLikeVideo(url) ? "video" : "image");

  const updateUrlKinds = (updater: (prev: Record<string, "image" | "video">) => Record<string, "image" | "video">) => {
    setUrlKinds(prev => {
      const next = updater(prev);
      onUrlKindsChange?.(next);
      return next;
    });
  };

  useEffect(() => {
    if (!onKindsChange) return;

    const hasVideo =
      uploads.some(u => u.status === "uploading" && u.isVideo) ||
      value.some(url => kindOfUrl(url) === "video");

    const hasPhoto =
      uploads.some(u => u.status === "uploading" && !u.isVideo) ||
      value.some(url => kindOfUrl(url) === "image");

    onKindsChange({ hasPhoto, hasVideo });
  }, [value, uploads, urlKinds, onKindsChange]);

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    const remainingSlots = Math.max(0, maxItems - (value.length + uploads.length));
    if (remainingSlots <= 0) return;
    const toUpload = Array.from(files).slice(0, remainingSlots);

    toUpload.forEach(file => {
      const id = `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const abort = new AbortController();
      const preview = URL.createObjectURL(file);
      const isVideo = /video/i.test(file.type) || /\.(mp4|mov|avi|mkv|webm)$/i.test(file.name);
      const item: UploadItem = {
        id,
        name: file.name,
        progress: 0,
        status: "uploading",
        abort,
        preview,
        isVideo
      };
      setUploads(prev => [...prev, item]);

      uploadMediaWithProgress(
        file,
        pct => setUploads(prev => prev.map(u => (u.id === id ? { ...u, progress: pct } : u))),
        abort.signal
      )
        .then(url => {
          setUploads(prev => prev.filter(u => u.id !== id));

          const kind: "image" | "video" = isVideo ? "video" : "image";
          setMediaKind(url, kind);
          updateUrlKinds(prev => ({ ...prev, [url]: kind }));

          onChange([...latestUrlsRef.current, url]);
          URL.revokeObjectURL(preview);
        })
        .catch(err => {
          const canceled = String(err?.message || "").toLowerCase().includes("cancel");
          setUploads(prev =>
            prev.map(u =>
              u.id === id
                ? {
                  ...u,
                  status: canceled ? "canceled" : "error",
                  error: canceled ? undefined : String(err)
                }
                : u
            )
          );
        });
    });
  };

  const removeUrl = (url: string) => {
    onChange(value.filter(u => u !== url));
    removeMediaKind(url);
    updateUrlKinds(prev => {
      const { [url]: _drop, ...rest } = prev;
      return rest;
    });
  };

  const cancelUpload = (id: string) => {
    setUploads(prev =>
      prev.map(u => {
        if (u.id === id && u.status === "uploading") u.abort.abort();
        return u.id === id ? { ...u, status: "canceled" } : u;
      })
    );
  };

  const reachedMax = value.length + uploads.length >= maxItems;

  return (
    <Box sx={styles.tiles}>
      {value.map(url => {
        const kindGuess = urlKinds[url] ?? getMediaKind(url) ?? (extLooksLikeVideo(url) ? "video" : "image");

        return (
          <Box key={url} sx={styles.tile}>
            <Box sx={styles.mediaBox}>
              {kindGuess === "video" ? (
                <video
                  src={url}
                  style={styles.media as any}
                  muted
                  playsInline
                  onError={() => {
                    setMediaKind(url, "image");
                    updateUrlKinds(prev => (prev[url] === "image" ? prev : { ...prev, [url]: "image" }));
                  }}
                />
              ) : (
                <img
                  src={url}
                  alt=""
                  style={styles.media as any}
                  onError={() => {
                    setMediaKind(url, "video");
                    updateUrlKinds(prev => (prev[url] === "video" ? prev : { ...prev, [url]: "video" }));
                  }}
                />
              )}
              {kindGuess === "video" && (
                <Box sx={styles.videoBadge}>
                  <PlayArrowIcon sx={styles.videoBadgeIcon} />
                </Box>
              )}
              <IconButton
                size="small"
                sx={styles.removeBtn}
                onClick={() => removeUrl(url)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        );
      })}

      {uploads.map(u => (
        <Box key={u.id} sx={styles.tile}>
          <Box sx={styles.mediaBox}>
            {u.preview ? (
              u.isVideo ? (
                <video src={u.preview} style={styles.media as any} muted playsInline />
              ) : (
                <img src={u.preview} alt="" style={styles.media as any} />
              )
            ) : (
              <Box sx={styles.placeholderInner}>
                <ImageOutlinedIcon sx={{ fontSize: 34, color: "text.disabled" }} />
              </Box>
            )}

            {u.isVideo && (
              <Box sx={styles.videoBadge}>
                <PlayArrowIcon sx={styles.videoBadgeIcon} />
              </Box>
            )}

            {u.status === "uploading" && (
              <Box sx={styles.overlay}>
                <CircularProgress
                  size={38}
                  variant="determinate"
                  value={u.progress}
                  sx={styles.progress}
                />
                <IconButton
                  size="small"
                  sx={styles.cancelBtn}
                  onClick={() => cancelUpload(u.id)}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {u.status === "error" && (
              <Box sx={styles.errorOverlay}>
                <Tooltip title={u.error || "Upload failed"}>
                  <IconButton
                    size="small"
                    sx={styles.errorClose}
                    onClick={() =>
                      setUploads(prev => prev.filter(x => x.id !== u.id))
                    }
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {u.status === "canceled" && (
              <Box sx={styles.canceledOverlay}>
                <IconButton
                  size="small"
                  sx={styles.errorClose}
                  onClick={() =>
                    setUploads(prev => prev.filter(x => x.id !== u.id))
                  }
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
      ))}

      {!reachedMax && (
        <Box sx={styles.tile}>
          <Box
            role="button"
            tabIndex={0}
            onClick={triggerFileDialog}
            onKeyDown={e =>
              e.key === "Enter" || e.key === " " ? triggerFileDialog() : undefined
            }
            sx={styles.addTile}
          >
            <ImageOutlinedIcon sx={{ fontSize: 34, color: "text.disabled" }} />
          </Box>
        </Box>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        hidden
        onChange={(e) => {
          handleFiles(e.target.files);
          if (inputRef.current) inputRef.current.value = "";
        }}
      />
    </Box>
  );
}

const TILE_SIZE = 120;
const TILE_GAP = 16;

const styles = {
  tiles: {
    display: "flex",
    flexWrap: "wrap"
  },
  tile: {
    width: TILE_SIZE,
    height: TILE_SIZE,
    position: "relative",
    marginRight: `${TILE_GAP}px`,
    marginBottom: `${TILE_GAP}px`
  },
  mediaBox: {
    width: "100%",
    height: "100%",
    borderRadius: 6,
    overflow: "hidden",
    position: "relative",
    bgcolor: "grey.100",
    border: "1px solid",
    borderColor: "divider"
  },
  media: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block"
  },
  videoBadge: {
    position: "absolute",
    left: 8,
    bottom: 8,
    width: 32,
    height: 32,
    borderRadius: "50%",
    bgcolor: "rgba(255,255,255,0.92)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 2px rgba(0,0,0,0.4)",
    zIndex: 3
  },
  videoBadgeIcon: {
    fontSize: 20,
    color: "rgba(0,0,0,0.8)"
  },
  removeBtn: {
    position: "absolute",
    top: 4,
    right: 4,
    bgcolor: "rgba(0,0,0,0.55)",
    color: "#fff",
    p: 0.3,
    "&:hover": { bgcolor: "rgba(0,0,0,0.75)" }
  },
  addTile: {
    width: "100%",
    height: "100%",
    borderRadius: 6,
    border: "2px dashed",
    borderColor: "divider",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    bgcolor: "grey.50",
    transition: "background-color .15s, border-color .15s",
    "&:hover": {
      bgcolor: "grey.100",
      borderColor: "text.secondary"
    },
    outline: "none"
  },
  overlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: "rgba(0,0,0,0.35)",
    zIndex: 2
  },
  progress: {
    color: "#fff"
  },
  cancelBtn: {
    position: "absolute",
    top: 4,
    right: 4,
    bgcolor: "rgba(0,0,0,0.55)",
    color: "#fff",
    p: 0.3,
    "&:hover": { bgcolor: "rgba(0,0,0,0.75)" }
  },
  errorOverlay: {
    position: "absolute",
    inset: 0,
    bgcolor: "rgba(211,47,47,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  canceledOverlay: {
    position: "absolute",
    inset: 0,
    bgcolor: "rgba(117,117,117,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  errorClose: {
    bgcolor: "rgba(0,0,0,0.55)",
    color: "#fff",
    "&:hover": { bgcolor: "rgba(0,0,0,0.75)" }
  },
  placeholderInner: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  }
};
